import Phaser from "phaser";
import { SCROLLABLE_LIST_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import { attachScrollCellDrag } from "./scrollListDragHandle.js";
import { previewSymbolFor } from "../data/modulePorts.js";
import { FONT_FAMILY, RARITY_COLOR, TYPO } from "./uiTokens.js";
import { t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
// 타일 메트릭 SSoT = battleTabLayout(순수). 튜토리얼 손가락 출발점(paletteFirstCell)과 같은 값을 공유.
import {
  PALETTE_TILE_W as TILE_W,
  PALETTE_TILE_H as TILE_H,
  PALETTE_TILE_GAP as TILE_GAP,
  PALETTE_HEADER_H as HEADER_H,
} from "./battleTabLayout.js";

export interface PaletteRow {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly name: string;
  readonly level: number;
  readonly rarity: string;
  readonly placed: boolean;
  /** 미리보기 심볼용 기본 모양(효과 shapePool[0]). 그리드 배치 후 탭으로 풀 내 순환. */
  readonly shapeId: string;
  /** 슬롯 점유 모양(single/vert2/horz2). 미리보기 심볼이 실제 배치 방향과 일치하도록 반영. */
  readonly footprintId: string;
}

export interface ModulePaletteViewCallbacks {
  readonly onDrop: (instanceId: string, pointerX: number, pointerY: number) => boolean | void;
  /** 타일 탭(드래그 아님) — 해당 instanceId의 정보 팝업 열기 (그리드 타일 탭과 동일). */
  readonly onModuleInfo: (instanceId: string) => void;
}

interface TileEntry {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  /** 현재 컨테이너 로컬 home 좌표(i 기반 그리드 위치). 드래그 스냅백 + relayout에 사용. */
  homeX: number;
  homeY: number;
  /** 현재 이 풀 셀이 표시 중인 row. 잉여 셀(rows 초과)은 undefined — 클릭/드롭 핸들러가 동적 조회. */
  row?: PaletteRow;
}

const POOL_OVERSCAN_ROWS = 2;

function paletteRowEqual(a: PaletteRow | undefined, b: PaletteRow | undefined): boolean {
  return (
    a === b ||
    (!!a &&
      !!b &&
      a.instanceId === b.instanceId &&
      a.definitionId === b.definitionId &&
      a.name === b.name &&
      a.level === b.level &&
      a.rarity === b.rarity &&
      a.placed === b.placed &&
      a.shapeId === b.shapeId &&
      a.footprintId === b.footprintId)
  );
}

/**
 * 전투 탭 보유 모듈 팔레트 — ScrollableList에 미배치 모듈 타일을 담아 드래그로 그리드에 배치한다.
 *
 * 셀 풀링: `update`는 destroy/recreate 없이 viewport+overscan 풀 셀(bg+label)을 재사용한다.
 * battle은 resident 탭이라 모듈 파괴/배치마다 50ms 배치로 update가 도는데, 과거 clearContent 전체 재생성은
 * 모듈이 많을수록 매번 200+ GameObject + 이벤트 재등록 → 모바일 렉. bounded pool + ScrollableList virtualization으로
 * 보이는 셀만 그린다. 드래그 핸들러는 풀 셀당 1회만 등록(i 캡처 동적 조회), 드래그 중 update는 보류.
 */
export class ModulePaletteView {
  private readonly header: Phaser.GameObjects.Text;
  private readonly scrollList: ScrollableList;
  private readonly tiles: TileEntry[] = [];
  /** 드래그 진행 중 — true면 update를 보류(드래그 셀 위치/row 갱신 충돌 방지). endDrag가 flush. */
  private dragging = false;
  private pendingRows: readonly PaletteRow[] | null = null;
  /** 탭 가시성 — false면 update의 rebuild를 스킵(비활성 탭이 refreshAll로 계속 rebuild되는 렉 방지). */
  private visible = true;
  /** 비활성 중 도착한 최신 rows — setVisible(true) 재활성 시 1회 적용. */
  private latestRows: readonly PaletteRow[] | null = null;
  /** 직전 실제 렌더 fingerprint — resident battle refresh의 동일 viewport 반복 렌더를 no-op 처리. */
  private lastAppliedLayoutKey = "";

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    private width: number,
    maxHeight: number,
    private readonly cb: ModulePaletteViewCallbacks,
  ) {
    const contentTop = y + HEADER_H;
    const scrollableH = maxHeight - HEADER_H;

    this.header = scene.add
      .text(x, y, t("palette.header"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.tiny,
        color: PALETTE.textSecondary,
      })
      .setDepth(3);

    this.scrollList = new ScrollableList(scene, x, contentTop, {
      width,
      height: scrollableH,
      barWidth: 4,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.5,
      barAlpha: 0.7,
      scrollSpeed: 36,
      // virtualization — 타일 1개 높이 여유로 viewport 밖 셀 컬링(모바일 렉 방지).
      itemMargin: TILE_H,
      // 드래그 가능한 셀이 Phaser top hit target이 되어야 한다. Viewport zone은 scene-level
      // 스크롤 판정에 필요 없고, 켜져 있으면 파이프 드래그 시작을 가로챌 수 있다.
      viewportInteractive: false,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);
    this.scrollList.on(SCROLLABLE_LIST_EVENT.Scroll, () => {
      if (!this.visible || this.dragging || !this.latestRows) return;
      this.applyRows(this.latestRows);
    });
  }

  private perRow(): number {
    return Math.max(1, Math.floor((this.width + TILE_GAP) / (TILE_W + TILE_GAP)));
  }

  /** i번째 row item의 그리드 로컬 위치(center-origin). 풀 셀은 scroll window에 맞춰 이 좌표로 재사용된다. */
  private tilePos(i: number): { lx: number; ly: number } {
    const perRow = this.perRow();
    const col = i % perRow;
    const r = Math.floor(i / perRow);
    return {
      lx: TILE_W / 2 + col * (TILE_W + TILE_GAP),
      ly: TILE_H / 2 + r * (TILE_H + TILE_GAP),
    };
  }

  private visibleWindow(
    rowsLength: number,
    perRow: number,
    totalRows: number,
    scrollY: number,
  ): { startIndex: number; poolCount: number } {
    if (rowsLength <= 0 || totalRows <= 0) return { startIndex: 0, poolCount: 0 };
    const stride = TILE_H + TILE_GAP;
    const visibleRows = Math.max(1, Math.ceil(this.scrollList.viewHeight / stride));
    const poolRows = Math.min(totalRows, visibleRows + POOL_OVERSCAN_ROWS * 2);
    const maxStartRow = Math.max(0, totalRows - poolRows);
    const startRow = Math.min(maxStartRow, Math.max(0, Math.floor(scrollY / stride) - POOL_OVERSCAN_ROWS));
    const startIndex = startRow * perRow;
    const poolCount = Math.max(0, Math.min(rowsLength - startIndex, poolRows * perRow));
    return { startIndex, poolCount };
  }

  update(rows: readonly PaletteRow[]): void {
    this.latestRows = rows; // 비활성·드래그 중에도 최신 데이터는 항상 보관
    // 비활성(탭 나감)이면 rebuild 스킵 — refreshAll이 50ms마다 호출해도 일하지 않는다(재진입 시 1회 적용).
    if (!this.visible) return;
    // 드래그 중 update는 보류 — 드래그 셀의 row/위치 갱신이 진행 중 드래그와 충돌하지 않게. endDrag가 flush.
    if (this.dragging) {
      this.pendingRows = rows;
      return;
    }
    this.applyRows(rows);
  }

  /** 실제 타일 그리드 갱신 — update(활성 시)와 setVisible(true) 재활성 시 공유. */
  private applyRows(rows: readonly PaletteRow[]): void {
    const perRow = this.perRow();
    const totalRows = Math.ceil(rows.length / perRow);
    const contentH = totalRows > 0 ? totalRows * (TILE_H + TILE_GAP) - TILE_GAP : 0;
    const rawScrollY = this.scrollList.scrollY;
    const scrollY = Number.isFinite(rawScrollY) ? rawScrollY : 0;
    const clampedScrollY = Math.min(scrollY, Math.max(0, contentH - this.scrollList.viewHeight));
    const { startIndex, poolCount } = this.visibleWindow(rows.length, perRow, totalRows, clampedScrollY);
    const layoutKey = `${this.width}|${this.scrollList.viewHeight}|${perRow}|${contentH}|${startIndex}|${poolCount}`;
    if (layoutKey === this.lastAppliedLayoutKey && this.visibleRowsEqual(rows, startIndex, poolCount)) return;
    this.lastAppliedLayoutKey = layoutKey;

    this.ensureTiles(poolCount);
    this.scrollList.setContentHeight(contentH);

    // 잉여 셀은 viewport 밖(contentH + vh + 여유)으로 보내 항상 컬링(virtualization invisible).
    const offY = contentH + this.scrollList.viewHeight + TILE_H * 2;
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.tiles.forEach((tile, i) => {
      const rowIndex = startIndex + i;
      if (i < poolCount && rowIndex < rows.length) {
        const { lx, ly } = this.tilePos(rowIndex);
        tile.homeX = lx;
        tile.homeY = ly;
        tile.row = rows[rowIndex];
        positions.push({ obj: tile.bg, lx, ly }, { obj: tile.label, lx, ly });
        this.renderTile(tile, rows[rowIndex]!);
      } else {
        tile.row = undefined;
        positions.push({ obj: tile.bg, lx: tile.homeX, ly: offY }, { obj: tile.label, lx: tile.homeX, ly: offY });
      }
    });
    this.scrollList.relayoutContent(positions);
  }

  private visibleRowsEqual(rows: readonly PaletteRow[], startIndex: number, poolCount: number): boolean {
    if (this.tiles.length < poolCount) return false;
    for (let i = 0; i < this.tiles.length; i++) {
      const expected = i < poolCount ? rows[startIndex + i] : undefined;
      if (!paletteRowEqual(this.tiles[i]?.row, expected)) return false;
    }
    return true;
  }

  /** 셀 풀을 count 이상으로 확장 — 부족분만 생성(bg/label/드래그 핸들러 1회). */
  private ensureTiles(count: number): void {
    while (this.tiles.length < count) {
      const i = this.tiles.length;
      const bg = this.scene.add
        .rectangle(0, 0, TILE_W, TILE_H, PALETTE_NUM.buttonBg)
        .setName("modulePaletteBg")
        .setStrokeStyle(1, PALETTE_NUM.borderSoft)
        .setAlpha(0.92)
        .setDepth(5);
      const label = this.scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.tiny,
          color: PALETTE.textPrimary,
          align: "center",
        })
        .setName("modulePaletteLabel")
        .setOrigin(0.5)
        .setDepth(5);
      this.scrollList.addContent(bg, 0, 0);
      this.scrollList.addContent(label, 0, 0);
      const tile: TileEntry = { bg, label, homeX: 0, homeY: 0 };
      this.tiles.push(tile);
      // 드래그/클릭 — i 캡처 + tile.row 동적 조회(풀 셀 재사용 시 stale instanceId 방지).
      attachScrollCellDrag(this.scene, this.scrollList, {
        bg,
        label,
        getHome: () => ({ x: this.tiles[i]!.homeX, y: this.tiles[i]!.homeY }),
        onClick: () => {
          const r = this.tiles[i]!.row;
          if (r) this.cb.onModuleInfo(r.instanceId);
        },
        onDrop: (px, py) => {
          const r = this.tiles[i]!.row;
          if (!r || r.placed) return false;
          return this.cb.onDrop(r.instanceId, px, py) === true;
        },
        onDragStart: () => {
          this.dragging = true;
        },
        onDragEnd: (dropHandled) => this.endDrag(dropHandled),
      });
    }
  }

  /** 활성 셀의 색/텍스트를 현재 row로 갱신(객체 재사용). */
  private renderTile(tile: TileEntry, row: PaletteRow): void {
    const symbol = previewSymbolFor(row.shapeId, row.footprintId);
    const rarityColor = RARITY_COLOR[row.rarity as keyof typeof RARITY_COLOR] ?? PALETTE.textPrimary;
    tile.bg
      .setFillStyle(row.placed ? PALETTE_NUM.surfaceDisabled : PALETTE_NUM.cellBg)
      .setStrokeStyle(1, row.placed ? PALETTE_NUM.buttonBorder : PALETTE_NUM.borderSoft)
      .setAlpha(row.placed ? 0.7 : 0.94);
    tile.label
      .setText(`${symbol}\n${t("palette.tile", { name: row.name, level: row.level })}`)
      .setColor(row.placed ? PALETTE.textFaint : rarityColor)
      .setAlpha(row.placed ? 0.68 : 1);
  }

  /** 드래그 종료 — 보류 플래그 해제 + 드래그 중 도착한 최신 rows를 1회 적용. */
  private endDrag(dropHandled = false): void {
    this.dragging = false;
    if (dropHandled) {
      this.pendingRows = null;
      return;
    }
    if (this.pendingRows) {
      const rows = this.pendingRows;
      this.pendingRows = null;
      this.update(rows);
    }
  }

  /** 언어 변경 — 헤더 + 활성 타일 라벨을 재사용 방식으로 setText(재생성 0). */
  applyLanguage(): void {
    this.header.setText(t("palette.header"));
    this.tiles.forEach((tile) => {
      if (tile.row) this.renderTile(tile, tile.row);
    });
  }

  /** 펼치기/접기 재배치 — 헤더+스크롤 뷰포트 이동/리사이즈 후 최신 rows 재적용(타일 perRow 재계산). */
  resize(x: number, y: number, width: number, height: number): void {
    this.width = width;
    this.header.setPosition(x, y);
    this.scrollList.resize(x, y + HEADER_H, width, height - HEADER_H);
    if (this.visible && this.latestRows) this.applyRows(this.latestRows);
  }

  setVisible(visible: boolean): void {
    const wasHidden = !this.visible;
    this.visible = visible;
    this.header
      .setVisible(visible)
      .setActive(visible);
    this.scrollList.setVisible(visible);
    // 비활성 동안 스킵된 update를 재진입 시 1회 반영(탭 나간 사이 변동된 인벤토리).
    if (visible && wasHidden && this.latestRows) this.applyRows(this.latestRows);
  }
}
